import React, { useState } from 'react';
import api from '../utils/apiClient';
import { useAuth } from '../hooks/useAuth';
import { normalizeRole } from '../utils/roles';
import { getErrorMessage } from '../utils/errorMessage';
import { getLocale } from '../utils/dateLocale';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  CircularProgress,
  Menu
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import KeyIcon from '@mui/icons-material/Key';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useTranslation } from 'react-i18next';
import { useNotifications } from '../context/NotificationContext';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const AgentControl = ({ shouldShowButton, isMobile, navSx = null }) => {
  const { showError, showSuccess } = useNotifications();
  const { user } = useAuth();
  const canProvisionAgents = ['admin', 'super_admin'].includes(normalizeRole(user?.role));
  const { t } = useTranslation('admin');
  const [agentDialogOpen, setAgentDialogOpen] = useState(false);
  const [viewMode, setViewMode] = useState('list'); // 'list', 'scan', 'create'
  const [ipRanges, setIpRanges] = useState(['']);
  const [isAgentScanning, setIsAgentScanning] = useState(false);
  const [agentHostings, setAgentHostings] = useState([]);
  const [selectedAgentHosting, setSelectedAgentHosting] = useState('');
  const [isLoadingAgentHostings, setIsLoadingAgentHostings] = useState(false);
  const [allAgents, setAllAgents] = useState([]);
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [isLoadingAgents, setIsLoadingAgents] = useState(false);
  const [visibleKeyAgentId, setVisibleKeyAgentId] = useState(null);
  const [agentKeys, setAgentKeys] = useState({}); // Хранилище ключей агентов
  const [loadingKeyAgentId, setLoadingKeyAgentId] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [newAgentHosting, setNewAgentHosting] = useState('');
  const [changeHostingDialogOpen, setChangeHostingDialogOpen] = useState(false);
  const [newHostingForAgent, setNewHostingForAgent] = useState('');
  const [newNameForAgent, setNewNameForAgent] = useState('');

  const handleAgentClick = async () => {
    setViewMode('list');
    setAgentDialogOpen(true);
    await Promise.all([loadAllAgents(), loadAgentHostings()]);
  };

  const loadAllAgents = async () => {
    setIsLoadingAgents(true);
    try {
      const response = await api.get(`${API_BASE_URL}data/agent`);

      const agents = response.data?.results || [];
      setAllAgents(agents);
    } catch (error) {
      console.error('Ошибка при загрузке агентов:', error);
      showError(t('agentErrorLoadAgents', { error: error.message }));
      setAllAgents([]);
    } finally {
      setIsLoadingAgents(false);
    }
  };

  const handleOpenScanModeForAgent = async (agent) => {
    // Устанавливаем выбранного агента и его хостинг
    setSelectedAgentId(agent.id);
    const hostingId = typeof agent.hosting === 'string'
      ? agent.hosting
      : agent.hosting?.id || agent.hosting_id;
    if (hostingId) {
      setSelectedAgentHosting(hostingId);
    }

    // Загружаем актуальные данные агента из API
    try {
      const response = await api.get(`${API_BASE_URL}data/agent`);
      const agents = response.data?.results || [];
      const freshAgent = agents.find(a => a.id === agent.id);

      if (freshAgent?.scan_range && Array.isArray(freshAgent.scan_range) && freshAgent.scan_range.length > 0) {
        setIpRanges(freshAgent.scan_range);
      } else {
        setIpRanges(['']);
      }
    } catch (error) {
      console.error('Ошибка при загрузке данных агента:', error);
      // Если не удалось загрузить из API, используем данные из текущего состояния
      if (agent.scan_range && Array.isArray(agent.scan_range) && agent.scan_range.length > 0) {
        setIpRanges(agent.scan_range);
      } else {
        setIpRanges(['']);
      }
    }

    setViewMode('scan');
  };

  const handleOpenCreateMode = () => {
    setViewMode('create');
    setNewAgentHosting('');
  };

  const handleBackToList = () => {
    setViewMode('list');
  };

  const loadAgentHostings = async () => {
    setIsLoadingAgentHostings(true);
    try {
      const response = await api.get(`${API_BASE_URL}agent/scan/info`);

      setAgentHostings(response.data.hostings || []);

      const hostings = response.data.hostings || [];
      if (hostings.length > 0) {
        setSelectedAgentHosting(hostings[0].id);
      } else {
        setSelectedAgentHosting('');
        setSelectedAgentId('');
      }
    } catch (error) {
      console.error('Ошибка при загрузке хостингов агента:', error);
      showError(t('agentErrorLoadHostings', { error: error.message }));
    } finally {
      setIsLoadingAgentHostings(false);
    }
  };


  const loadAgentKey = async (agentId) => {
    if (agentKeys[agentId]) {
      // Ключ уже загружен, просто показываем/скрываем
      setVisibleKeyAgentId(prev => prev === agentId ? null : agentId);
      return;
    }

    setLoadingKeyAgentId(agentId);
    try {
      const response = await api.get(`${API_BASE_URL}data/agent/${agentId}/key`);

      const key = response.data?.key || response.data?.token || t('agentKeyNotFound');
      setAgentKeys(prev => ({ ...prev, [agentId]: key }));
      setVisibleKeyAgentId(agentId);
    } catch (error) {
      console.error('Ошибка при загрузке ключа агента:', error);
      showError(t('agentErrorLoadKey', { error: getErrorMessage(error) }));
      setVisibleKeyAgentId(null);
    } finally {
      setLoadingKeyAgentId(null);
    }
  };

  const toggleKeyVisibility = (agentId) => {
    if (visibleKeyAgentId === agentId) {
      // Скрываем ключ
      setVisibleKeyAgentId(null);
    } else {
      // Загружаем и показываем ключ
      loadAgentKey(agentId);
    }
  };

  const handleMenuOpen = (event, agent) => {
    setAnchorEl(event.currentTarget);
    setSelectedAgent(agent);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedAgent(null);
  };

  const handleOpenChangeHostingDialog = () => {
    if (!selectedAgent) {
      console.warn('Не выбран агент для изменения хостинга');
      showError(t('agentErrorNotSelected'));
      handleMenuClose();
      return;
    }
    
    // hosting может быть строкой (ID) или объектом с полем id
    const currentHostingId = typeof selectedAgent?.hosting === 'string'
      ? selectedAgent.hosting
      : selectedAgent?.hosting?.id || selectedAgent?.hosting_id || '';
    
    console.log('Открытие диалога изменения хостинга:', {
      agentId: selectedAgent.id,
      agentName: selectedAgent.name,
      currentHostingId
    });
    
    // Сохраняем данные агента перед закрытием меню
    const agentToChange = selectedAgent;
    
    // Закрываем меню (это НЕ очищает selectedAgent, т.к. мы его не сбрасываем здесь)
    setAnchorEl(null);
    
    // Устанавливаем значения и открываем диалог
    setNewHostingForAgent(currentHostingId);
    setNewNameForAgent(selectedAgent?.name || '');
    setChangeHostingDialogOpen(true);
    // selectedAgent остается установленным для использования в диалоге
  };

  const handleCloseChangeHostingDialog = () => {
    setChangeHostingDialogOpen(false);
    setNewHostingForAgent('');
    setNewNameForAgent('');
    setSelectedAgent(null);
  };

  const handleDeleteAgent = async () => {
    if (!selectedAgent) return;
    // Удаление необратимо: площадка перестаёт присылать данные, пока не заведут нового агента
    if (!window.confirm(t('agentDeleteConfirm', { name: selectedAgent.name || selectedAgent.id }))) return;

    try {
      await api.delete(`${API_BASE_URL}data/agent/${selectedAgent.id}`);

      // Удаляем ключ из хранилища
      if (selectedAgent?.id) {
        setAgentKeys(prev => {
          const newKeys = { ...prev };
          delete newKeys[selectedAgent.id];
          return newKeys;
        });
        if (visibleKeyAgentId === selectedAgent.id) {
          setVisibleKeyAgentId(null);
        }
      }

      showSuccess(t('agentSuccessDeleted'));
      await loadAllAgents();
      handleMenuClose();
    } catch (error) {
      console.error('Ошибка при удалении агента:', error);
      if (error.response?.status === 409) {
        const errorMessage = getErrorMessage(error, t('agentErrorDeleteConflict'));
        showError(errorMessage);
      } else {
        showError(t('agentErrorDelete', { error: getErrorMessage(error) }));
      }
    }
  };

  const handleChangeHosting = async () => {
    // Сохраняем ID агента и данные локально, так как selectedAgent может быть очищен
    const agentId = selectedAgent?.id;
    const newHostingId = newHostingForAgent?.trim();
    const newAgentName = newNameForAgent?.trim() || null;

    if (!agentId || !newHostingId) {
      console.warn('Недостаточно данных для изменения агента:', { agentId, newHostingId });
      showError(t('agentErrorChangeNoData'));
      return;
    }

    // Проверяем, что хотя бы одно поле изменилось
    const currentHostingId = typeof selectedAgent?.hosting === 'string'
      ? selectedAgent.hosting
      : selectedAgent?.hosting?.id || selectedAgent?.hosting_id || '';

    const currentName = selectedAgent?.name || '';
    const hostingChanged = String(currentHostingId) !== String(newHostingId);
    const nameChanged = currentName !== newAgentName;

    if (!hostingChanged && !nameChanged) {
      showError(t('agentErrorChangeNoChanges'));
      return;
    }

    try {
      // Формируем тело запроса с только измененными полями
      const updateData = {};
      if (hostingChanged) {
        updateData.hosting_id = newHostingId;
      }
      if (nameChanged) {
        updateData.name = newAgentName;
      }

      console.log('Отправка запроса на изменение агента:', {
        agentId,
        updateData,
        url: `${API_BASE_URL}data/agent/${agentId}`
      });

      const response = await api.patch(`${API_BASE_URL}data/agent/${agentId}`, updateData);

      console.log('Ответ сервера:', response.data);

      const changes = [];
      if (hostingChanged) changes.push(t('agentHostingLabel').toLowerCase());
      if (nameChanged) changes.push(t('agentEditNameLabel').toLowerCase());
      showSuccess(t('agentSuccessUpdated', { changes: changes.join(', ') }));

      // Обновляем список агентов для отображения актуальных данных
      await loadAllAgents();
      // Очищаем выбранного агента и закрываем диалог
      handleCloseChangeHostingDialog();
    } catch (error) {
      console.error('Ошибка при изменении агента:', error);
      // Показываем сообщение об ошибке из ответа сервера или общее сообщение
      const errorMessage = getErrorMessage(error);
      showError(t('agentErrorChange', { error: errorMessage }));
    }
  };

  const handleCreateAgent = async () => {
    if (!newAgentHosting) {
      showError(t('agentErrorSelectHosting'));
      return;
    }

    try {
      const response = await api.post(`${API_BASE_URL}data/agent`, {
        hosting_id: newAgentHosting
      });

      // Сохраняем ключ, если он был возвращен
      const agentKey = response.data?.key || response.data?.token;
      if (agentKey && response.data?.id) {
        setAgentKeys(prev => ({ ...prev, [response.data.id]: agentKey }));
      }

      showSuccess(t('agentSuccessCreated'));
      await loadAllAgents();
      setViewMode('list');
      setNewAgentHosting('');

      // Если ключ был возвращен, автоматически показываем его
      if (agentKey && response.data?.id) {
        setVisibleKeyAgentId(response.data.id);
      }
    } catch (error) {
      console.error('Ошибка при создании агента:', error);
      showError(t('agentErrorCreate', { error: getErrorMessage(error) }));
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString(getLocale(), {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleAddIpRange = () => {
    setIpRanges([...ipRanges, '']);
  };

  const handleRemoveIpRange = (index) => {
    const newIpRanges = ipRanges.filter((_, i) => i !== index);
    setIpRanges(newIpRanges.length > 0 ? newIpRanges : ['']);
  };

  const handleIpRangeChange = (index, value) => {
    const newIpRanges = [...ipRanges];
    newIpRanges[index] = value;
    setIpRanges(newIpRanges);
  };

  const handleStartScan = async () => {
    const validIpRanges = ipRanges.filter(range => range.trim() !== '');

    if (validIpRanges.length === 0) {
      showError(t('agentErrorScanNoRanges'));
      return;
    }

    if (!selectedAgentHosting) {
      showError(t('agentErrorScanNoHosting'));
      return;
    }

    if (!selectedAgentId) {
      showError(t('agentErrorScanNoAgent'));
      return;
    }

    setIsAgentScanning(true);
    try {
      // Задаем scan_range для агента через v2 API
      // Параметр называется ip_ranges, но на бэкенде сохраняется в scan_range
      const payload = {
        agent_id: selectedAgentId,
        ip_ranges: validIpRanges.map(range => range.trim())
      };

      await api.post(`${API_BASE_URL}v2/agent/scan`, payload);

      showSuccess(t('agentScanRangeSet'));

      // Обновляем список агентов, чтобы отобразить новый scan_range
      await loadAllAgents();

      setViewMode('list');
      setIpRanges(['']);
      setSelectedAgentHosting('');
      setSelectedAgentId('');
    } catch (error) {
      console.error('Ошибка при задании диапазона сканирования:', error);
      showError(t('agentErrorScanRange', { error: error.message }));
    } finally {
      setIsAgentScanning(false);
    }
  };

  const getAgentsForHosting = (hostingId) => {
    if (!hostingId) return [];
    return allAgents.filter(agent => {
      // hosting может быть строкой (ID) или объектом с полем id
      const agentHostingId = typeof agent.hosting === 'string'
        ? agent.hosting
        : agent.hosting?.id || agent.hosting_id;
      return agentHostingId === hostingId;
    });
  };

  if (!shouldShowButton) {
    return null;
  }

  const availableAgents = getAgentsForHosting(selectedAgentHosting);

  const renderListMode = () => (
    <>
      {isLoadingAgents ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : allAgents.length === 0 ? (
        <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
          {t('agentNoAgents')}
        </Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('agentColumnId')}</TableCell>
                <TableCell>{t('agentColumnName')}</TableCell>
                <TableCell>{t('agentColumnHosting')}</TableCell>
                <TableCell>{t('agentColumnScanRange')}</TableCell>
                <TableCell>{t('agentColumnCreatedAt')}</TableCell>
                <TableCell>{t('agentColumnLastPing')}</TableCell>
                <TableCell align="center">{t('agentColumnDevices')}</TableCell>
                <TableCell align="center">{t('agentColumnKey')}</TableCell>
                <TableCell align="center">{t('agentColumnActions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {allAgents.map((agent) => {
                const agentId = agent.id || '';
                const shortId = agentId.slice(-6);
                const isKeyVisible = visibleKeyAgentId === agentId;
                
                return (
                  <TableRow key={agentId} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {shortId}
                      </Typography>
                    </TableCell>
                    <TableCell>{agent.name || 'N/A'}</TableCell>
                    <TableCell>
                      {agent.hosting_name ||
                       (typeof agent.hosting === 'object' && agent.hosting?.name) ||
                       (typeof agent.hosting === 'string' && agentHostings.find(h => h.id === agent.hosting)?.name) ||
                       agent.hosting ||
                       'N/A'}
                    </TableCell>
                    <TableCell>
                      {agent.scan_range && Array.isArray(agent.scan_range) && agent.scan_range.length > 0 ? (
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                          {agent.scan_range.join(', ')}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          {t('agentScanRangeNotSet')}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{formatDate(agent.created_at)}</TableCell>
                    <TableCell>{formatDate(agent.last_ping || agent.lastPing)}</TableCell>
                    <TableCell align="center">{agent.device_count || agent.deviceCount || 0}</TableCell>
                    <TableCell align="center">
                      <Tooltip title={isKeyVisible ? t('agentTooltipHideKey') : t('agentTooltipShowKey')}>
                        <IconButton
                          size="small"
                          onClick={() => toggleKeyVisibility(agentId)}
                          color={isKeyVisible ? "primary" : "default"}
                          disabled={loadingKeyAgentId === agentId}
                        >
                          {loadingKeyAgentId === agentId ? (
                            <CircularProgress size={16} />
                          ) : (
                            <KeyIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, agent)}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      
      {/* Показываем ключ выбранного агента */}
      {visibleKeyAgentId && (() => {
        const agent = allAgents.find(a => a.id === visibleKeyAgentId);
        if (!agent) return null;
        
        const agentKey = agentKeys[visibleKeyAgentId];
        const isLoading = loadingKeyAgentId === visibleKeyAgentId;
        
        return (
          <Box
            sx={{
              p: 2,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              backgroundColor: 'background.paper',
              mt: 2
            }}
          >
            <Typography variant="subtitle2" gutterBottom>
              {t('agentKeyTitle', { name: agent.name || 'N/A', id: visibleKeyAgentId.slice(-6) })}
            </Typography>
            {isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <Typography
                variant="body2"
                sx={{
                  fontFamily: 'monospace',
                  wordBreak: 'break-all',
                  backgroundColor: 'action.hover',
                  p: 1,
                  borderRadius: 1
                }}
              >
                {agentKey || t('agentKeyNotFound')}
              </Typography>
            )}
          </Box>
        );
      })()}
    </>
  );

  const renderScanMode = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <FormControl fullWidth size="small">
        <InputLabel>{t('agentHostingLabel')}</InputLabel>
        <Select
          value={selectedAgentHosting}
          label={t('agentHostingLabel')}
          disabled={true}
        >
          {isLoadingAgentHostings ? (
            <MenuItem disabled>{t('agentLoadingHostings')}</MenuItem>
          ) : agentHostings.length === 0 ? (
            <MenuItem disabled>{t('agentNoHostings')}</MenuItem>
          ) : (
            agentHostings.map((hosting) => (
              <MenuItem key={hosting.id} value={hosting.id}>
                {hosting.name}
              </MenuItem>
            ))
          )}
        </Select>
      </FormControl>

      <FormControl fullWidth size="small">
        <InputLabel>{t('agentButton')}</InputLabel>
        <Select
          value={selectedAgentId}
          label={t('agentButton')}
          disabled={true}
          displayEmpty
        >
          {availableAgents.length === 0 ? (
            <MenuItem disabled>{t('agentNoAgents')}</MenuItem>
          ) : (
            availableAgents.map((agent) => {
              const suffix = agent.id ? agent.id.slice(-6) : '';
              return (
                <MenuItem key={agent.id} value={agent.id}>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                    <Typography variant="body2">{agent.name}</Typography>
                    {suffix && (
                      <Typography variant="caption" color="text.secondary">
                        ({suffix})
                      </Typography>
                    )}
                  </Box>
                </MenuItem>
              );
            })
          )}
        </Select>
      </FormControl>

      <Typography variant="body2" color="text.secondary">
        {t('agentScanHelpText')}
      </Typography>
      <Box sx={{ pl: 2, mb: 2 }}>
        <Typography variant="body2" component="div">
          {t('agentScanFormatIp')}<code>192.168.1.100</code>
        </Typography>
        <Typography variant="body2" component="div">
          {t('agentScanFormatRange')}<code>192.168.1.1-192.168.1.50</code>
        </Typography>
        <Typography variant="body2" component="div">
          {t('agentScanFormatCidr')}<code>192.168.0.0/24</code>
        </Typography>
      </Box>
      
      {ipRanges.map((range, index) => (
        <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField
            label={t('agentIpRangeLabel', { index: index + 1 })}
            value={range}
            onChange={(e) => handleIpRangeChange(index, e.target.value)}
            placeholder="192.168.1.0/24"
            fullWidth
            size="small"
          />
          {ipRanges.length > 1 && (
            <Button
              variant="outlined"
              color="error"
              size="small"
              onClick={() => handleRemoveIpRange(index)}
              sx={{ minWidth: 'auto', px: 1 }}
            >
              ✕
            </Button>
          )}
        </Box>
      ))}
      
      <Button
        variant="outlined"
        onClick={handleAddIpRange}
        sx={{ alignSelf: 'flex-start' }}
      >
        {t('agentAddIpRange')}
      </Button>
    </Box>
  );

  const renderCreateMode = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="body2" color="text.secondary">
        {t('agentCreateSelectHosting')}
      </Typography>
      <FormControl fullWidth size="small">
        <InputLabel>{t('agentHostingLabel')}</InputLabel>
        <Select
          value={newAgentHosting}
          onChange={(e) => setNewAgentHosting(e.target.value)}
          label={t('agentHostingLabel')}
          disabled={isLoadingAgentHostings}
        >
          {isLoadingAgentHostings ? (
            <MenuItem disabled>{t('agentLoadingHostings')}</MenuItem>
          ) : agentHostings.length === 0 ? (
            <MenuItem disabled>{t('agentNoHostings')}</MenuItem>
          ) : (
            agentHostings.map((hosting) => (
              <MenuItem key={hosting.id} value={hosting.id}>
                {hosting.name}
              </MenuItem>
            ))
          )}
        </Select>
      </FormControl>
    </Box>
  );

  return (
    <>
      {navSx ? (
        <Button sx={navSx} onClick={handleAgentClick}>{t('agentsNav')}</Button>
      ) : isMobile ? (
        <Tooltip title={t('agentTooltip')}>
          <IconButton size="small" onClick={handleAgentClick}>
            <SearchIcon />
          </IconButton>
        </Tooltip>
      ) : (
        <Button
          variant="outlined"
          startIcon={<SearchIcon />}
          size="small"
          onClick={handleAgentClick}
        >
          {t('agentButton')}
        </Button>
      )}

      {/* Единый диалог с переключением режимов */}
      <Dialog open={agentDialogOpen} onClose={() => setAgentDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          {viewMode === 'list' && t('agentDialogListTitle')}
          {viewMode === 'scan' && (() => {
            const agent = allAgents.find(a => a.id === selectedAgentId);
            return agent ? t('agentDialogScanTitleWithName', { name: agent.name }) : t('agentDialogScanTitle');
          })()}
          {viewMode === 'create' && t('agentDialogCreateTitle')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {viewMode === 'list' && renderListMode()}
            {viewMode === 'scan' && renderScanMode()}
            {viewMode === 'create' && renderCreateMode()}
          </Box>
        </DialogContent>
        <DialogActions>
          {viewMode === 'list' ? (
            <>
              <Button onClick={() => setAgentDialogOpen(false)}>
                {t('usersCancelButton')}
              </Button>
              {canProvisionAgents && <Button
                onClick={handleOpenCreateMode}
                variant="contained"
                startIcon={<AddIcon />}
              >
                {t('agentCreateButton')}
              </Button>}
            </>
          ) : viewMode === 'scan' ? (
            <>
              <Button onClick={handleBackToList}>
                {t('usersCancelButton')}
              </Button>
              <Button
                onClick={handleStartScan}
                variant="contained"
                disabled={isAgentScanning}
              >
                {isAgentScanning ? t('agentScanSaving') : t('agentScanSetRange')}
              </Button>
            </>
          ) : (
            <>
              <Button onClick={handleBackToList}>
                {t('usersCancelButton')}
              </Button>
              <Button
                onClick={handleCreateAgent}
                variant="contained"
                disabled={!newAgentHosting}
              >
                {t('adminControlCreateButton')}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Меню действий с агентом */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={async () => {
          if (selectedAgent) {
            handleMenuClose();
            await handleOpenScanModeForAgent(selectedAgent);
          }
        }}>
          <SearchIcon fontSize="small" sx={{ mr: 1 }} />
          {t('agentMenuSetScanRange')}
        </MenuItem>
        {canProvisionAgents && <MenuItem onClick={handleOpenChangeHostingDialog}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          {t('agentMenuEdit')}
        </MenuItem>}
        <MenuItem onClick={handleDeleteAgent} sx={{ color: 'error.main' }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          {t('agentMenuDelete')}
        </MenuItem>
      </Menu>

      {/* Диалог смены хостинга */}
      <Dialog 
        open={changeHostingDialogOpen} 
        onClose={handleCloseChangeHostingDialog}
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>
          {t('agentEditDialogTitle')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {selectedAgent && (
              <Typography variant="body2" color="text.secondary">
                {t('agentEditIdLabel')}<strong>{selectedAgent.id?.slice(-6)}</strong>
              </Typography>
            )}
            <TextField
              label={t('agentEditNameLabel')}
              value={newNameForAgent}
              onChange={(e) => setNewNameForAgent(e.target.value)}
              fullWidth
              size="small"
              placeholder={t('agentEditNamePlaceholder')}
            />
            <FormControl fullWidth size="small">
              <InputLabel>{t('agentHostingLabel')}</InputLabel>
              <Select
                value={newHostingForAgent}
                onChange={(e) => setNewHostingForAgent(e.target.value)}
                label={t('agentHostingLabel')}
                disabled={isLoadingAgentHostings}
              >
                {isLoadingAgentHostings ? (
                  <MenuItem disabled>{t('agentLoadingHostings')}</MenuItem>
                ) : agentHostings.length === 0 ? (
                  <MenuItem disabled>{t('agentNoHostings')}</MenuItem>
                ) : (
                  agentHostings.map((hosting) => (
                    <MenuItem key={hosting.id} value={hosting.id}>
                      {hosting.name}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseChangeHostingDialog}>
            {t('usersCancelButton')}
          </Button>
          <Button 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              console.log('Кнопка "Изменить" нажата:', {
                selectedAgent: selectedAgent?.id,
                newHostingForAgent,
                newNameForAgent
              });
              handleChangeHosting();
            }} 
            variant="contained"
            disabled={!newHostingForAgent || !selectedAgent}
          >
            {t('agentEditButton')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AgentControl;

